import * as EventTracker from '@src/analytics/EventTracker';
import {
  useDatoClient,
  useTemplatedPrompts,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { ContentTab } from '@src/types.ts/general';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import ChatGPTService from '../../../services/ChatGPTService';
import { useFlagsCombination } from '../../../utility/useFlagsCombination';
import FullScreenLoadingOverlay from '../../common/FullScreenLoadingOverlay';
import { useCompositionContext } from '../../sidepanel/CompositionContext';
import SpinningLoading from '../../SpinningLoading';
import AIProducerModal from '../AIProducerModal';
import BrandVoiceRevisionModal from '../BrandVoiceRevisionModal';
import ContentActions from '../ContentActions';
import EditSharableImageModal from './EditSharableImageModal';
import PullQuoteResultsModal from './PullQuoteResultsModal';
import QuoteCard from './QuoteCard';
import QuoteCardOLD from './QuoteCardOLD';

import type { Artifact, ShareableImageType } from '../../../types.ts/story';
import type { AudienceOption } from '../../common/AudienceSelector';
import type { GoalOption } from '../../common/GoalSelector';
import type { PhotoSourceSelection } from '../../common/PhotoSourceFilterComponent';
import type { CompositionTemplate } from '../../sidepanel/CompositionSettings/types';
import type { Speaker } from '../SpeakerTypes';
import type { Renderer } from '@src/renderer/Renderer';
import type { AIPrompt } from '@src/types.ts/ai_prompts';
import type { FC, MutableRefObject } from 'react';

const logger = LogManager.getLogger('PullQuotes');

function extractImpactScores(quote: string): {
  strippedQuote: string;
  impactScores: Record<string, number> | undefined;
} {
  let strippedQuote = quote;
  let impactScores: Record<string, number> | undefined;

  // Format 1 (preferred): [SCORES:{"key1":85,"key2":70,...}]
  // Allow trailing quotes, periods, punctuation, or whitespace after the closing ]
  const jsonScoresRegex = /\[SCORES:(\{[^}]+\})\]["'.,;:!?\s]*$/;
  const jsonMatch = strippedQuote.match(jsonScoresRegex);
  if (jsonMatch && jsonMatch[1]) {
    try {
      // Normalize backslash-escaped keys: {\Impact\:96} → {"Impact":96}
      const normalized = jsonMatch[1].replace(/\\([^\\]+)\\/g, '"$1"');
      const parsed = JSON.parse(normalized);
      if (parsed && typeof parsed === 'object') {
        impactScores = parsed as Record<string, number>;
      }
    } catch {
      // Expected during streaming — partial JSON arrives before the full score object
    }
    // Always strip score tag from displayed text — never show raw [SCORES:...] to user
    strippedQuote = strippedQuote.replace(jsonScoresRegex, '').trim();
    strippedQuote = strippedQuote.replace(/\|+\s*$/, '').trim();
  } else {
    // Format 2 (legacy fallback): [SCORE:XX]
    const scoreRegex = /\[SCORE:(\d+)\]\s*$/;
    const scoreMatch = strippedQuote.match(scoreRegex);
    if (scoreMatch && scoreMatch[1]) {
      impactScores = { impact: parseInt(scoreMatch[1], 10) };
      strippedQuote = strippedQuote.replace(scoreRegex, '').trim();
    }
  }

  return { strippedQuote, impactScores };
}

const PullQuotes: FC<{}> = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();
  const userIdentity = useUserIdentity();
  const [searchParams, setSearchParams] = useSearchParams();
  const gptServiceRef = useRef(
    new ChatGPTService(videoCreator, datoClientStore.aiPromptRepository),
  );
  const gptService = gptServiceRef.current;
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSpeakers, setSelectedSpeakers] = useState<Speaker[]>([]);
  const [isSpeakerFilterActive, setIsSpeakerFilterActive] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<GoalOption | null>(null);
  const [selectedAudiences, setSelectedAudiences] = useState<AudienceOption[]>(
    [],
  );
  const [selectedPhotoSources, setSelectedPhotoSources] =
    useState<PhotoSourceSelection>({
      brandKit: true,
      story: true,
    });
  const { story, unsavedShareableImages } = videoCreator;

  const {
    enableShareableImageRegeneration,
    enableShareableImageCompositions,
    contentStudioQuotesEnableGlobalTemplates,
    contentStudioContentProducingEnableCustom,
    contentStudioPullQuotesEnablePhotoSources,
  } = useFlagsCombination(userIdentity.currentRole);
  const enableGlobalTemplates =
    contentStudioQuotesEnableGlobalTemplates ?? true;

  const { compositions, isLoading: isLoadingCompositions } =
    useCompositionContext();

  // Filter out global preset compositions when the flag is off
  const filteredCompositions = useMemo(() => {
    if (enableGlobalTemplates) return compositions;
    return compositions.filter(comp => !comp.isGlobalPreset);
  }, [compositions, enableGlobalTemplates]);

  const [showResultsModal, setShowResultsModal] = useState(false);
  const [generationSource, setGenerationSource] = useState<{
    type: 'original' | 'clip';
    title: string;
  }>({ type: 'original', title: '' });

  const contentGenerated = videoCreator.contentStudioGeneratedContent;

  const [loadingStock, setLoadingStock] = useState(false);

  const generatedContent = contentGenerated?.Quotes?.content?.response;
  const usedSpeakers = contentGenerated?.Quotes?.usedSpeakers;
  const generatedContentLanguage =
    contentGenerated?.Quotes?.selectedLanguage || null;

  const artifacts = (
    story?.storyArtifacts?.map((artifact: Artifact) => {
      if (artifact.responsiveImage) {
        return artifact;
      }
    }) || []
  ).filter(Boolean);
  const organizationArtifacts =
    story?._allReferencingShowcases?.flatMap((album: any) =>
      (album.organizationArtifacts || []).filter((a: any) => a.responsiveImage),
    ) || [];
  const assets = (
    story?.storyAssets?.map(asset => {
      if (asset.responsiveImage) {
        return asset;
      }
    }) || []
  ).filter(Boolean);

  const [promptTemplate, setPromptTemplate] = useState<AIPrompt | undefined>();
  const [customPrompt, setCustomPrompt] = useState<string | undefined>();

  useEffect(() => {
    let quoteList = generatedContent as string[];
    if (typeof generatedContent === 'string') {
      const lines = generatedContent.split('\n').filter(q => q.trim());
      const lastLineStreaming = isLoading && !generatedContent.endsWith('\n');
      quoteList = lines.flatMap((q, i) => {
        const trimmed = q.trim();
        const scoresAt = trimmed.indexOf('[SCORES:');
        // The quote and attribution are done once the score tag starts: show
        // the card now, without scores, and let them fill in when the tag
        // closes. Stripping to the same text the complete line yields keeps
        // the card's id (and its rendered canvas) stable across that change.
        if (scoresAt >= 0 && !trimmed.includes('}]')) {
          return [
            trimmed
              .slice(0, scoresAt)
              .replace(/\|+\s*$/, '')
              .trim(),
          ];
        }
        // A line still being written changes every token, which would mint
        // a new card id and remount its canvas each time; wait for its tag.
        if (scoresAt < 0 && lastLineStreaming && i === lines.length - 1) {
          return [];
        }
        return [q];
      });
    }

    const saved = story?.shareableImages || [];
    const existingUnsaved = videoCreator.unsavedShareableImages || [];

    const unsaved: any = [];
    const stillImages = [
      ...(selectedPhotoSources.story ? [...artifacts, ...assets] : []),
      ...(selectedPhotoSources.brandKit ? organizationArtifacts : []),
    ];
    for (const [index, quote] of (quoteList || []).entries()) {
      const image = (
        stillImages.length ? stillImages[index % stillImages.length] : null
      ) as Artifact | null;

      const isInCard = saved.some(
        q =>
          q.quote?.trim() === quote.trim() &&
          q.imagefile?.url === (image?.url || ''),
      );

      if (isInCard) continue;

      // Strip leading/trailing quotes, whitespace, dashes, and pipes from the quote
      let cleanedQuote = quote
        ?.replace(
          /^["'\u201C\u201D\u2018\u2019\s-]+|["'\u201C\u201D\u2018\u2019\s-]+$/g,
          '',
        )
        ?.trim();
      let storytellerName = '';

      const { strippedQuote, impactScores } = extractImpactScores(cleanedQuote);
      cleanedQuote = strippedQuote;

      if (selectedSpeakers.length > 0 || usedSpeakers) {
        // Parse the quote to extract speaker name using the " --- Speaker Name" format
        const attributionRegex = /^(.*?)\s+---\s+(.+)$/;
        const match = cleanedQuote.match(attributionRegex);

        if (match && match[2]) {
          // Speaker name found in the format " --- Speaker Name"
          // Trim any extra quote characters that might be present in the speaker name
          storytellerName = match[2].trim().replace(/["']/g, '');
          // Remove the speaker attribution from the quote
          cleanedQuote = match[1].trim();
          // console.log(`Extracted speaker "${storytellerName}" from quote`);
        } else {
          // console.log(`No speaker attribution found in quote: "${quote.substring(0, 100)}..."`);
        }
      }

      // Preserve existing ID if the same quote+image+storyteller combination already exists
      // This prevents React from treating it as a new item and re-rendering
      // But if storytellerName changed (e.g., was incorrectly parsed before), generate new ID to force re-render
      // Strip leading/trailing quotes
      const finalCleanedQuote = cleanedQuote
        ?.replace(
          /^["'\u201C\u201D\u2018\u2019\s-]+|["'\u201C\u201D\u2018\u2019\s-]+$/g,
          '',
        )
        ?.trim();
      const existingImage = existingUnsaved.find(
        existing =>
          existing.quote?.trim() === finalCleanedQuote &&
          existing.imagefile?.url === (image?.url || '') &&
          existing.storytellerName === storytellerName,
      );

      unsaved.push({
        id: existingImage?.id || uuid(), // Reuse existing ID or generate new one
        quote: finalCleanedQuote,
        storytellerName,
        metadata: {
          ...(impactScores !== undefined && { impactScore: impactScores }),
          ...(promptTemplate?.title && { theme: promptTemplate.title }),
          ...(customPrompt && { customPrompt }),
        },
        ...(image && {
          imagefile: {
            ...image,
          },
        }),
      });
    }

    videoCreator.unsavedShareableImages = unsaved;
  }, [
    isLoading,
    generatedContent,
    selectedSpeakers,
    usedSpeakers,
    selectedPhotoSources,
    promptTemplate,
    customPrompt,
  ]);

  const { aiShareableImagesTemplates } = useTemplatedPrompts();
  const [aiProducerOpen, setAiProducerOpen] = useState(false);

  // Auto-open AI producer when navigating with ?produce=true
  useEffect(() => {
    if (
      searchParams.get('produce') === 'true' &&
      searchParams.get('tab') === ContentTab.pull_quotes
    ) {
      setAiProducerOpen(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('produce');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // State for EditSharableImageModal - lifted from QuoteCard to prevent unmount on save
  const [editSharableImage, setEditSharableImage] = useState<{
    data: ShareableImageType;
    isSaved: boolean;
    compositionTemplate: CompositionTemplate;
    source: Record<string, any>;
    storyteller?: string;
  } | null>(null);

  // Deep-link: open quote editor when navigating from Recent Content with quoteId param
  useEffect(() => {
    if (editSharableImage) return; // already open
    const quoteId = searchParams.get('quoteId');
    if (!quoteId || !story?.shareableImages || !filteredCompositions?.length)
      return;

    const quote = story.shareableImages.find(img => img.id === quoteId);
    if (!quote) return;

    const defaultPullQuoteId =
      videoCreator.organization?.defaultPullQuoteTemplate?.id;
    const compositionTemplate =
      filteredCompositions.find(t => t.id === quote.parentComposition?.id) ||
      (defaultPullQuoteId
        ? filteredCompositions.find(t => t.id === defaultPullQuoteId)
        : undefined) ||
      filteredCompositions.find(t => !t.isGlobalPreset) ||
      filteredCompositions.find(
        t => t.isGlobalPreset && t.internalName.includes('Default'),
      ) ||
      filteredCompositions.find(t => t.isGlobalPreset) ||
      filteredCompositions[0];

    setEditSharableImage({
      data: {
        id: quote.id,
        imagefile: quote.imagefile,
        quote: quote.quote,
        storytellerName: quote.storytellerName,
        speaker: quote.speaker,
        metadata: quote.metadata || {},
        source: quote.source || {},
      },
      isSaved: true,
      compositionTemplate,
      source: quote.source || {},
      storyteller: quote.storytellerName || '',
    });

    // Clear the quoteId from URL after consuming it
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('quoteId');
    setSearchParams(newParams, { replace: true });
  }, [
    searchParams,
    story?.shareableImages,
    filteredCompositions,
    editSharableImage,
  ]);

  // Map to store refs for each QuoteCard's renderer and handlers
  // PR4 analytics: store topics applied at the last Generate so the
  // subsequent Regenerate event can include them too.
  const latestSelectedTopicsRef = useRef<string[] | undefined>(undefined);

  const quoteCardRefsMap = useRef<
    Map<
      string,
      {
        rendererRef: MutableRefObject<Renderer | null>;
        handleRerender: (source: Record<string, any>) => void;
        handleAddComposition: (composition: CompositionTemplate) => void;
        setCompositionTemplate: (composition: CompositionTemplate) => void;
      }
    >
  >(new Map());

  /**
   * Helper function to safely get card refs from the ref map.
   * Logs a warning if refs are not found to aid debugging.
   */
  const getCardRefs = (cardId: string, context: string) => {
    const cardRefs = quoteCardRefsMap.current.get(cardId);
    if (!cardRefs) {
      logger.warn(
        `Card refs not found for cardId "${cardId}" in context: ${context}`,
      );
    }
    return cardRefs;
  };

  /**
   * Resolve the composition template for a given card, using the same
   * priority logic as QuoteCard: parentComposition → org default → user
   * template → global default → first available.
   */
  const resolveCompositionTemplate = useCallback(
    (card: ShareableImageType): CompositionTemplate | undefined => {
      const defaultPullQuoteId =
        videoCreator.organization?.defaultPullQuoteTemplate?.id;
      return (
        filteredCompositions.find(t => t.id === card.parentComposition?.id) ||
        (defaultPullQuoteId
          ? filteredCompositions.find(t => t.id === defaultPullQuoteId)
          : undefined) ||
        filteredCompositions.find(t => !t.isGlobalPreset) ||
        filteredCompositions.find(
          t => t.isGlobalPreset && t.internalName.includes('Default'),
        ) ||
        filteredCompositions.find(t => t.isGlobalPreset) ||
        filteredCompositions[0]
      );
    },
    [filteredCompositions, videoCreator.organization],
  );

  // Listen for 'open-pull-quote' event to auto-open a specific pull quote
  // in the edit modal (e.g., after saving from transcript context menu)
  useEffect(() => {
    const handleOpenPullQuote = (e: Event) => {
      const quoteId = (e as CustomEvent<string>).detail;
      if (!quoteId) return;

      const savedCards = story?.shareableImages;
      if (!savedCards) return;

      const card = savedCards.find(img => img.id === quoteId);
      if (!card) {
        logger.warn(`open-pull-quote: card not found for id "${quoteId}"`);
        return;
      }

      const template = resolveCompositionTemplate(card);
      if (!template) {
        logger.warn(
          `open-pull-quote: no composition template available for card "${quoteId}"`,
        );
        return;
      }

      setEditSharableImage({
        data: {
          id: card.id,
          imagefile: card.imagefile,
          quote: card.quote,
          storytellerName: card.storytellerName,
          speaker: card.speaker,
          metadata: card.metadata || {},
          source: card.source || {},
        },
        isSaved: true,
        compositionTemplate: template,
        source: card.source || {},
        storyteller: card.storytellerName || '',
      });
    };

    window.addEventListener('open-pull-quote', handleOpenPullQuote);
    return () =>
      window.removeEventListener('open-pull-quote', handleOpenPullQuote);
  }, [story?.shareableImages, resolveCompositionTemplate]);

  const handleGenerate = async (
    template?: AIPrompt,
    brandVoice?: string,
    customInstructions?: string,
    avoidWords?: string,
    chosenSpeakers: Speaker[] = [],
    selectedGoal?: GoalOption | null,
    selectedAudiences?: AudienceOption[],
    selectedLanguage?: string,
    photoSources?: PhotoSourceSelection,
    clipTranscript?: string,
    speakerFilterActive?: boolean,
    selectedTopics?: string[],
  ) => {
    latestSelectedTopicsRef.current = selectedTopics;
    setIsLoading(true);

    setShowResultsModal(true);
    setSelectedSpeakers(chosenSpeakers);
    setIsSpeakerFilterActive(!!speakerFilterActive);
    // Before the stream: cards are selectable while it runs, so their metadata
    // must carry this run's theme, not the previous one's.
    setCustomPrompt(customInstructions || undefined);
    setPromptTemplate(template);
    if (selectedGoal) setSelectedGoal(selectedGoal);
    if (selectedAudiences) setSelectedAudiences(selectedAudiences);
    if (photoSources) setSelectedPhotoSources(photoSources);

    if (clipTranscript) {
      const clip = videoCreator.story?.otherVideos?.find(
        v => v.transcriptionText === clipTranscript,
      );
      setGenerationSource({
        type: 'clip',
        title: clip?.title || 'Clip',
      });
    } else {
      setGenerationSource({
        type: 'original',
        title: videoCreator.story?.title || 'Original',
      });
    }

    EventTracker.trackPullQuoteGenerationStarted({
      story_id: story?.id,
      story_title: story?.title,
      template: template?.title,
      custom_instructions: customInstructions,
      speaker_count: chosenSpeakers?.length,
      selected_language: selectedLanguage,
      selected_topics: selectedTopics,
    });

    try {
      if (clipTranscript) {
        await gptService.regenerateFromClipTranscript({
          key: 'Quotes',
          type: 'new',
          setLoading: setIsLoading,
          template,
          customInstructions,
          brandVoice,
          avoidWords,
          selectedSpeakers: chosenSpeakers,
          speakerFilterActive,
          selectedGoal,
          selectedAudiences,
          selectedLanguage,
          clipTranscript,
        });
      } else {
        await gptService.regenerateStreamResponse({
          key: 'Quotes',
          type: 'new',
          setLoading: setIsLoading,
          template,
          customInstructions,
          brandVoice,
          avoidWords,
          selectedSpeakers: chosenSpeakers,
          speakerFilterActive,
          selectedGoal,
          selectedAudiences,
          selectedLanguage,
        });
      }
      EventTracker.trackPullQuoteGenerationCompleted({
        story_id: story?.id,
        quotes_count: videoCreator.unsavedShareableImages?.length || 0,
      });
    } catch (error) {
      EventTracker.trackPullQuoteGenerationFailed({
        story_id: story?.id,
        error_message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const [revisionModalOpen, setRevisionModalOpen] = useState(false);
  const handleRegenerate = async (
    revision: string,
    options: { selectedLanguage?: string | null },
  ) => {
    setIsLoading(true);
    setRevisionModalOpen(false);
    EventTracker.trackPullQuoteRegenerationStarted({
      story_id: story?.id,
      story_title: videoCreator.story?.title,
      template: promptTemplate?.title,
      custom_instructions: customPrompt,
      revision,
      selected_language:
        options.selectedLanguage ?? generatedContentLanguage ?? undefined,
      selected_topics: latestSelectedTopicsRef.current,
    });
    await gptService.regenerateStreamResponse({
      key: 'Quotes',
      type: 'regenerate',
      setLoading: setIsLoading,
      template: promptTemplate,
      customInstructions: customPrompt,
      revision,
      selectedSpeakers,
      speakerFilterActive: isSpeakerFilterActive,
      selectedGoal,
      selectedAudiences,
      selectedLanguage: options.selectedLanguage || generatedContentLanguage,
    });
  };

  return (
    <Main data-testid="pull-quotes-section">
      {/* Only a stock-photo fetch blocks the whole app. Generation used to raise
          this overlay too, but the results modal opens immediately and streams
          the quotes in with its own indicator — so the overlay was redundant
          behind the modal, and stayed stuck on screen (isLoading only clears
          when the stream ends) if the modal was closed mid-generation
          (PLAT-11232). */}
      {loadingStock && <FullScreenLoadingOverlay text="Loading stock photo…" />}
      {enableShareableImageRegeneration && (
        <ContentActions
          data-testid="pull-quotes-generate-button"
          onGenerate={() => {
            EventTracker.trackPullQuoteProducerOpened({
              story_id: story?.id,
            });
            setAiProducerOpen(true);
          }}
          renderLeft={
            story?.shareableImages && story?.shareableImages?.length > 0
              ? () => (
                  <QuotesTitle>
                    Pull Quotes ({story?.shareableImages?.length})
                  </QuotesTitle>
                )
              : undefined
          }
        />
      )}
      {enableShareableImageCompositions ? (
        <QuotesWrapper>
          {isLoadingCompositions &&
          !(filteredCompositions && filteredCompositions.length > 0) ? (
            <SpinningLoading text="Loading pull quotes…" />
          ) : filteredCompositions && filteredCompositions.length > 0 ? (
            <QuotesWrapper>
              {story?.shareableImages && story?.shareableImages?.length > 0 && (
                <>
                  <Quotes>
                    {story?.shareableImages?.map(content => (
                      <QuoteCard
                        // Key on the stable id only. Including _updatedAt caused
                        // the card to remount on every save (save bumps
                        // _updatedAt), re-creating the renderer and visibly
                        // bouncing the just-dragged position. Post-save preview
                        // updates flow through the in-place handleRerender call
                        // in onSaved instead.
                        key={content.id}
                        card={content}
                        isSaved
                        setLoadingStock={setLoadingStock}
                        compositionTemplates={filteredCompositions}
                        onEditModalOpen={setEditSharableImage}
                        registerCardRefs={(id, refs) =>
                          quoteCardRefsMap.current.set(id, refs)
                        }
                        unregisterCardRefs={id =>
                          quoteCardRefsMap.current.delete(id)
                        }
                      />
                    ))}
                  </Quotes>
                </>
              )}
            </QuotesWrapper>
          ) : (
            // No composition templates available after loading
            <NoTemplatesMessage>
              No shareable image templates are configured. Please add templates
              in DatoCMS.
            </NoTemplatesMessage>
          )}
        </QuotesWrapper>
      ) : (
        <Quotes>
          {story?.shareableImages?.map(content => (
            <QuoteCardOLD
              key={content.id}
              id={content.id}
              text={content.quote}
              imageFile={content.imagefile}
              allReferencingSharedContents={
                content._allReferencingSharedContents
              }
              allReferencingCaptions={content._allReferencingCaptions}
              isSaved
              setLoadingStock={setLoadingStock}
            />
          ))}
        </Quotes>
      )}

      {aiProducerOpen ? (
        <AIProducerModal
          customType={
            contentStudioContentProducingEnableCustom ? 'Quotes' : undefined
          }
          title="Let's find some quotes"
          templatesSublabel="Theme"
          templates={aiShareableImagesTemplates || []}
          onClose={() => setAiProducerOpen(false)}
          onConfirm={handleGenerate}
          allowBrandVoice={false}
          enableSpeakerSelection // Enable speaker selection for quotes
          enablePhotoSourceSelection={contentStudioPullQuotesEnablePhotoSources}
        />
      ) : null}

      {revisionModalOpen ? (
        <BrandVoiceRevisionModal
          title="Regenerate quotes"
          selectedLanguage={generatedContentLanguage}
          onClose={() => setRevisionModalOpen(false)}
          handleGenerate={handleRegenerate}
        />
      ) : null}

      {showResultsModal && (
        <PullQuoteResultsModal
          quotes={unsavedShareableImages || []}
          isLoading={isLoading}
          onClose={() => {
            setShowResultsModal(false);
            videoCreator.unsavedShareableImages = null as any;
          }}
          compositionTemplates={filteredCompositions}
          generationContext={{
            source: generationSource.type,
            sourceTitle: generationSource.title,
            theme: promptTemplate?.title,
            customPrompt,
            speakers: selectedSpeakers
              .map(s => s.name || s.id)
              .filter((v): v is string => !!v),
            goal: selectedGoal?.label,
            audiences: selectedAudiences
              .map(a => a.label)
              .filter((v): v is string => !!v),
          }}
        />
      )}

      {editSharableImage && (
        <EditSharableImageModal
          data={editSharableImage.data}
          onClose={() => setEditSharableImage(null)}
          storyteller={editSharableImage.storyteller}
          isSaved={editSharableImage.isSaved}
          compositionTemplate={editSharableImage.compositionTemplate}
          source={editSharableImage.source}
          onSaved={(
            id: string,
            source: Record<string, any>,
            composition: CompositionTemplate,
          ) => {
            // Find the current card ID - it might have changed if it was unsaved and is now saved
            let currentCardId = id;

            const savedCardIndex = story?.shareableImages?.findIndex(
              img => img.id === currentCardId,
            );

            if (savedCardIndex !== undefined && savedCardIndex !== -1) {
              const savedCard = story!.shareableImages[savedCardIndex];
              story!.shareableImages[savedCardIndex] = {
                ...savedCard,
                source,
                _updatedAt: new Date().toISOString(),
                parentComposition: { id: composition.id },
              };
            }

            // Keep the modal open but update isSaved state if it was unsaved
            if (!editSharableImage.isSaved) {
              // Find the saved card in the saved list
              let savedCard;
              if (savedCardIndex !== undefined && savedCardIndex !== -1) {
                savedCard = story!.shareableImages[savedCardIndex];
                savedCard.parentComposition = { id: composition.id };
              } else {
                savedCard = story!.shareableImages.find(
                  img => img.quote === editSharableImage.data.quote,
                );
              }

              if (savedCard) {
                currentCardId = savedCard.id;
                setEditSharableImage({
                  ...editSharableImage,
                  data: savedCard,
                  isSaved: true,
                  source,
                  compositionTemplate: composition,
                });
              } else {
                logger.warn(
                  'Saved card not found after save operation. Quote:',
                  editSharableImage.data.quote?.substring(0, 50),
                );
              }
            }
            // Update the renderer of the current card using the ref map
            const cardRefs = getCardRefs(currentCardId, 'onSaved callback');
            if (cardRefs) {
              cardRefs.handleRerender(source);
            }
          }}
          onAfterCreate={(composition: CompositionTemplate) => {
            // Update the modal state with the new composition
            setEditSharableImage({
              ...editSharableImage,
              compositionTemplate: composition,
            });

            // Update the composition template in the QuoteCard using the ref map
            const cardRefs = getCardRefs(
              editSharableImage.data.id,
              'onAfterCreate callback',
            );
            if (cardRefs) {
              cardRefs.setCompositionTemplate(composition);
            }
          }}
        />
      )}
    </Main>
  );
});

export default PullQuotes;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0;
  gap: 20px;
  .buttons {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }
`;

const QuotesWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const QuotesTitle = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
`;

const Quotes = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin: 0 auto;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
`;

const NoTemplatesMessage = styled.p`
  color: var(--text-muted);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  padding: 20px 0;
`;
